import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Newspaper } from 'lucide-react';
import { HOMEPAGE_OUTLETS, PRESS_RELEASE_DATE, PRESS_STATS } from '../data/press';

const EASE = [0.25, 0, 0, 1];

/**
 * Homepage "Featured in" strip. Outlet names are set as wordmarks in type
 * rather than logos, so no third-party brand assets ship with the site. Each
 * name links straight to that outlet's copy of the story; the footer line
 * links to the full /press page.
 */
const PressSection = () => (
  <section data-testid="press-section" className="py-20 px-6 bg-[#0f0f0f] border-y border-white/5">
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.7, ease: EASE }}
        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
      >
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/[0.06] mb-5">
            <Newspaper className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-blue-400 text-xs font-medium tracking-widest uppercase">In the press</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Featured in India&apos;s leading newsrooms.
          </h2>
        </div>
        <p className="text-gray-400 text-sm md:text-base leading-relaxed md:max-w-sm">
          ClearWay&apos;s launch announcement on {PRESS_RELEASE_DATE} was carried by{' '}
          <span className="text-white font-medium">{PRESS_STATS[0].value} outlets</span>, including
          national newspapers, business dailies and all three Indian wire agencies.
        </p>
      </motion.div>

      <ul
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/[0.06] border border-white/[0.06] rounded-2xl overflow-hidden"
        aria-label="Outlets that covered ClearWay"
      >
        {HOMEPAGE_OUTLETS.map((outlet, i) => (
          <motion.li
            key={outlet.name}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.55, delay: 0.04 * i, ease: EASE }}
            className="bg-[#0f0f0f]"
          >
            <a
              href={outlet.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col justify-between h-full min-h-[7rem] p-5 hover:bg-white/[0.03] focus-visible:bg-white/[0.03] outline-none transition-colors duration-300"
              aria-label={`${outlet.name} — read ClearWay coverage (opens in a new tab)`}
            >
              <span className="flex items-start justify-between gap-2">
                <span className="text-white text-base md:text-lg font-semibold tracking-tight leading-snug">
                  {outlet.name}
                </span>
                <ArrowUpRight
                  className="w-4 h-4 text-gray-600 group-hover:text-blue-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 flex-shrink-0 mt-1"
                  aria-hidden="true"
                />
              </span>
              <span className="text-[11px] uppercase tracking-widest text-gray-500 mt-4">{outlet.kind}</span>
            </a>
          </motion.li>
        ))}
      </ul>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <p className="text-xs text-gray-600">
          Also covered independently by ElectronicsForU, BriefGlance and StartupLanes.
        </p>
        <Link
          to="/press"
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
        >
          See all press coverage
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </motion.div>
    </div>
  </section>
);

export default PressSection;
